// src/pages/CookiePage.js
import React from 'react';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom'; // Import Link for internal routing
import './CookiesPage.css'; // Specific styles for this page
import '../index.css';       // General styles

const CookiePage = () => {
  // Function to get the current date for "Last updated"
  const getFormattedDate = () => {
    const date = new Date();
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  return (
    <>
      <main className="cookiePageContainer">
        <section className="cookieHero">
          {/* H1 Title */}
          <h1 className="cookieTitle">
            Our Cookie Policy
          </h1>
          {/* Last Updated Date */}
          <p className="cookieLastUpdated">
            Last updated: {getFormattedDate()}
          </p>
        </section>

        {/* Section 1: About this policy */}
        <section className="cookieSection">
          <h2>1. About this policy</h2>
          <p>
            This Cookie Policy explains how Nexed (“we”, “our”, “us”) uses cookies and similar tracking technologies on nexed.org (the “Site”). It should be read together with our <Link to="/privacy">Privacy Policy</Link>, which describes in more detail how we process personal data.
          </p>
        </section>

        {/* Section 2: What are cookies? */}
        <section className="cookieSection">
          <h2>2. What are cookies?</h2>
          <p>
            Cookies are small text files that a website stores on your device so it can remember information about you, such as your language preference or whether you are logged in. First-party cookies are set by the site you are visiting; third-party cookies are set by external services whose content appears on the site (e.g., analytics scripts, embedded video). Some cookies expire when you close your browser (“session” cookies), while others remain until a set expiry date (“persistent” cookies). See more at: <a href="https://developer.mozilla.org/en-US/docs/Web/HTTP/Cookies" target="_blank" rel="noopener noreferrer">developer.mozilla.org</a>
          </p>
        </section>

        {/* Section 3: Why we use cookies - Table */}
        <section className="cookieSection">
          <h2>3. Why we use cookies</h2>
          <p>We group the cookies we use into four broad categories:</p>
          <div className="tableWrapper">
            <table className="cookieTable">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Purpose</th>
                  <th>Do they collect personal data?*</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Strictly necessary</td>
                  <td>Enable core features such as page navigation, secure log-in, and form submission (e.g., our Strapi CMS session cookie).</td>
                  <td>Only the data needed to make the Site work.</td>
                </tr>
                <tr>
                  <td>Performance &amp; analytics</td>
                  <td>Help us understand how visitors interact with the Site so we can improve it (e.g., Google Analytics, Plausible).</td>
                  <td>Yes – but we receive reports in aggregated form and do not attempt to identify individual users.</td>
                </tr>
                <tr>
                  <td>Functionality</td>
                  <td>Remember choices you make (e.g., language selection) so the Site can tailor content to you.</td>
                  <td>Possibly, depending on the setting you choose.</td>
                </tr>
                <tr>
                  <td>Marketing / third-party</td>
                  <td>Allow us and our partners (e.g., LinkedIn Insight Tag) to build an anonymised profile of your interests so we can show relevant content or ads elsewhere.</td>
                  <td>Yes – only with your explicit consent.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="tableNote">
            *“Personal data” means any information that can identify you directly or indirectly (GDPR Art. 4(1)).
          </p>
        </section>

        {/* Section 4: Cookies we may set - Table */}
        <section className="cookieSection">
          <h2>4. Cookies we may set</h2>
          <p>The exact cookies in use can change as we refine our platform. Typical examples include:</p>
          <div className="tableWrapper">
            <table className="cookieTable">
              <thead>
                <tr>
                  <th>Cookie name</th>
                  <th>Provider</th>
                  <th>Category</th>
                  <th>Expiry</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>nexed_session</td>
                  <td>Nexed (first-party)</td>
                  <td>Strictly necessary</td>
                  <td>Session</td>
                </tr>
                <tr>
                  <td>_ga, _gid, _gat_*</td>
                  <td>Google Analytics</td>
                  <td>Performance</td>
                  <td>24 months / 24 hours / 1 minute</td>
                </tr>
                <tr>
                  <td>cookieConsentStatus</td>
                  <td>Nexed</td>
                  <td>Strictly necessary</td>
                  <td>12 months</td>
                </tr>
                <tr>
                  <td>jwtToken</td>
                  <td>Nexed API (Strapi)</td>
                  <td>Strictly necessary</td>
                  <td>7 days</td>
                </tr>
                <tr>
                  <td>li_fat_id</td>
                  <td>LinkedIn</td>
                  <td>Marketing</td>
                  <td>30 days</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="tableNote">
            (Cookie names may vary as we roll out new features. Our banner always reflects the up-to-date list.)
          </p>
        </section>

        {/* Section 5: Third-party cookies we cannot control */}
        <section className="cookieSection">
          <h2>5. Third-party cookies we cannot control</h2>
          <p>
            Embedded content (e.g., YouTube, Vimeo, or X/Twitter feeds) and analytics/advertising tags set their own cookies. These are governed by the privacy policies of those providers. We block all non-essential third-party tags until you grant consent via our cookie banner, in line with GDPR and CPRA requirements. More information: <a href="https://www.bigid.com/" target="_blank" rel="noopener noreferrer">bigid.com</a>
          </p>
        </section>

        {/* Section 6: Managing your cookie preferences */}
        <section className="cookieSection">
          <h2>6. Managing your cookie preferences</h2>
          <h3>Consent banner</h3>
          <p>
            On your first visit (and whenever we materially change the cookie set) we display a banner that lets you:
          </p>
          <ul>
            <li>Accept all cookies</li>
            <li>Reject non-essential cookies</li>
            <li>Customise settings per category</li>
          </ul>

          <h3>Browser controls</h3>
          <p>
            Most browsers let you reject or delete cookies altogether. Visit your browser’s help section for specific instructions.
          </p>

          <h3>Opt-outs from third parties</h3>
          <ul>
            <li>Google Analytics: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">https://tools.google.com/dlpage/gaoptout</a></li>
            <li>LinkedIn ads: turn off “Ads on LinkedIn based on your activity” in your LinkedIn settings.</li>
          </ul>
          <p>
            Rejecting or deleting cookies may affect Site functionality.
          </p>
        </section>

        {/* Section 7: International visitors */}
        <section className="cookieSection">
          <h2>7. International visitors</h2>
          <p>Our servers are located in Canada, but we serve visitors worldwide. We therefore honour:</p>
          <ul>
            <li>General Data Protection Regulation (GDPR) for EU/EEA &amp; UK users</li>
            <li>California Consumer Privacy Act (CCPA/CPRA) for California residents</li>
            <li>Other local laws such as LGPD (Brazil) and PIPEDA (Canada)</li>
          </ul>
          <p>
            If local law sets a stricter standard than this Policy, we follow the stricter requirement.
          </p>
        </section>

        {/* Section 8: Updates to this policy */}
        <section className="cookieSection">
          <h2>8. Updates to this policy</h2>
          <p>
            We may update this Policy to reflect changes in technology, law, or our business operations. The “Last updated” date at the top indicates when the Policy was last revised. Material changes will be highlighted in a banner or similar notice on the Site for at least 30 days.
          </p>
        </section>
      </main>

      {/* Footer at the bottom */}
      <Footer />
    </>
  );
};

export default CookiePage;
